import { DateOfBirthFieldSchema } from '@afterpaytouch/form'
import { TFunction } from 'next-i18next'
import { RegisterOptions, ValidationValueMessage } from 'react-hook-form'
import { Country } from '@afterpaytouch/portal-api/types'
import { COUNTRY_TO_NAME_RESTRICTIONS } from './constants'
import { NameField } from './types'
import { COUNTRY_TO_LICENCE_VERSION_RESTRICTIONS } from '../DriversLicence/constants'

export const namePattern =
  (t: TFunction) =>
  (name: string): RegisterOptions => {
    // This pattern validates for characters, spaces, digits, hyphens, apostrophes and ampersands

    // For example
    //     aC12 3'456
    //     Hg&6-543''21
    //     1334DG -F343

    // Not allowed:
    //     Ac24*2343
    //     ∴a892ljaa
    //     *&23LKJ@@
    const validationRegex = /^$|^[a-zA-Z\u00C0-\u017F][a-zA-Z\u00C0-\u017F- ']*$/

    return {
      pattern: {
        value: validationRegex,
        // The name should match the localisations in 'verifyIdentity:error:invalidName_${name}`
        message: t('verifyIdentity:error:invalidName', { context: name }),
      },
    }
  }

export const nationalIdPattern = (t: TFunction) => (): RegisterOptions => {
  /**
   * References:
   * https://www.ssa.gov/employer/randomization.html
   * https://www.kevinleary.net/blog/social-security-number-validation-rules-regex/
   *
   * This pattern validates for:
   * 1. Should not allow non-numeric value
   * 2. Should not starts with 666, 000, 999
   * 3. Should not ends with 0000 or contains 000
   * 4. Only accepts dash(-) as seperator or no seperator
   */

  // For example
  // Allowed:
  //     123-45-6789
  //     123456789

  // Not allowed:
  //     666-12-3456
  //     666-12-34567
  //     000-12-3456
  //     999-12-3456
  //     333-44-0000
  //     œ121121231
  //     123/45/6789
  const validationRegex = /^(?!666|000|9\d{2})\d{3}[- ]{0,1}(?!00)\d{2}[- ]{0,1}(?!0{4})\d{4}$/

  return {
    pattern: {
      value: validationRegex,
      message: t('verifyIdentity:error:invalidNationalId'),
    },
  }
}

export const requiredValidation =
  (t: TFunction) =>
  (name: string): RegisterOptions => ({
    // The name should match the localisations in 'verifyIdentity:error:required_${name}`
    required: t('verifyIdentity:error:required', { context: name }) as unknown as string,
  })

export const dobValidation =
  (t: TFunction, country?: Country) =>
  (name: string): Omit<DateOfBirthFieldSchema, 'type'> => {
    const minAge = 18
    const maxAge = country === Country.NZ ? 120 : 90

    return {
      minAge: {
        value: minAge,
        message: t('verifyIdentity:error:minAge', { context: name, minAge }),
      },
      maxAge: {
        value: maxAge,
        message: t('verifyIdentity:error:maxAge', { context: name, maxAge }),
      },
    }
  }

export const nameMaxLengthValidation =
  (t: TFunction) =>
  (name: NameField, country?: Country): RegisterOptions => {
    const key = country ?? ''
    const maxLengthMap = COUNTRY_TO_NAME_RESTRICTIONS[key] ?? COUNTRY_TO_NAME_RESTRICTIONS.DEFAULT
    const value = maxLengthMap[name].maxLength

    return {
      maxLength: {
        value,
        message: t('verifyIdentity:error:nameTooLong', { context: name }),
      },
    }
  }

export const licenceVersionLengthValidation =
  (t: TFunction) =>
  (country?: Country): { minLength: ValidationValueMessage<number>; maxLength: ValidationValueMessage<number> } => {
    const key = country ?? ''
    const lengthMap = COUNTRY_TO_LICENCE_VERSION_RESTRICTIONS[key] ?? COUNTRY_TO_LICENCE_VERSION_RESTRICTIONS.DEFAULT
    const minValue = lengthMap.minLength
    const maxValue = lengthMap.maxLength

    return {
      minLength: {
        value: minValue,
        message: t('verifyIdentity:licence:invalidVersion'),
      },
      maxLength: {
        value: maxValue,
        message: t('verifyIdentity:licence:invalidVersion'),
      },
    }
  }

export const expiryDateValidation = () => (validationMessage: string) => ({
  validate: {
    expired: (date: string) => new Date(date).getTime() - new Date().getTime() > 0 || validationMessage,
  },
})
